import React, {useEffect, useState} from "react";
import {changePageSizeState} from "../../../Slices/PageSlice";
import PageSizeState from "../../../Constants/Base/PageSizeState";
import {useDispatch} from "react-redux";
import {deleteAsset, getAssetList} from "../../../Services/AssetManagement/AssetService";
import TablePage from "../../../Components/DataGrid/TablePage";
import strings from "../../../localization";
import {formatColumnObject, formatColumnDate} from "../../../Components/DataGrid/ValueFormatter";
import {changeBreadcrumbs} from "../../../Slices/BreadcrumbsSlice";
import Modules from "../../../Constants/Base/Modules";
import AddAsset from "./AddAsset";
import TablePageContext, {
    FilterDefaults,
    TableDataDefaults,
    TablePageOptionDefaults
} from "../../../Context/TablePageContext";
import PageState from "../../../Constants/Base/PageState";
import EditAsset from "./EditAsset";
import ReferenceType from "../../../Constants/DocumentManagement/ReferenceType";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderColumnNumber } from "../../../Components/DataGrid/ValueCellRender";
import AppPermissions from "../../../Constants/Permissions/Permissions";
import Tag from "../../../Components/Forms/Pages/Document/Tag/Tag";
import {getUserCompanyAccess, getUserCompanyAccessById} from "../../../Services/User/UserCompanyAccessService";
import {fetchOptions} from '../../../Services/Base/FilterOptionsService';

const tableDescription = [

    {
        field: 'serialNumber', headerName: strings.pages.assetManagement.asset.assetList.serialNumber,
        width: 250,
    },
    {
        field: 'price', headerName: strings.pages.assetManagement.asset.assetList.price,
        renderCell: (params) => renderColumnNumber(params),
        width: 250,
    },
    {
        field: 'tax', headerName: strings.pages.assetManagement.asset.assetList.tax,
        renderCell: (params) => renderColumnNumber(params),
        width: 250,
    },
    {
        field: 'purchasedDate',
        headerName: strings.pages.assetManagement.asset.assetList.purchasedDate,
        width: 200,
        valueFormatter: (params) => formatColumnDate(params)
    },
    {
        field: 'warrantyEnd', headerName: strings.pages.assetManagement.asset.assetList.warrantyEnd,
        width: 250,
    },
    {
        field: 'location',
        headerName: strings.pages.assetManagement.asset.assetList.location,
        valueFormatter: (params) => formatColumnObject(params, 'name'),
        width: 250
    },
    {
        field: 'type',
        headerName: strings.pages.assetManagement.asset.assetList.type,
        valueFormatter: (params) => formatColumnObject(params, 'name'),
        width: 250
    },
    {
        field: 'user', headerName: strings.pages.hr.presence.presenceList.employee,
        valueFormatter: (params) => formatColumnObject(params, 'fullName'),
        width: 200
    },
];

const filters = []

const AssetList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [updatedFilter, setUpdatedFilter] = useState(!Boolean(searchParams.toString()));
    const [columns, setColumns] = useState([]);
    const [pageState, setPageState] = useState(PageState.View);
    const [filter, setFilter] = useState(FilterDefaults);
    const [tableData, setTableData] = useState(TableDataDefaults);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedItems, setSelectedItems] = useState(null);
    const [tablePageOptions, setTablePageOptions] = useState(TablePageOptionDefaults);
    const referenceType = ReferenceType.ASSET;
    const permissionGroup = AppPermissions.ASSET.GROUP;
    const [filterOptions, setFilterOptions] = useState({});

    const value = {
        tablePageOptions, setTablePageOptions,
        selectionModel, setSelectionModel,
        selectedItems, setSelectedItems,
        filter, setFilter, pageState, setPageState, showDeleteDialog, setShowDeleteDialog,
        navigate, updatedFilter, setUpdatedFilter
    }

    useEffect(() => {
        setColumns([...tableDescription]);
        dispatch(changePageSizeState(PageSizeState.SHORT));
        dispatch(changeBreadcrumbs({
            title: strings.pages.assetManagement.asset.assetList.pageTitle,
            hierarchy:[
                {label: strings.navigation.managmentTag},
                {label: Modules.ASSETS},
                {label: strings.pages.assetManagement.asset.assetList.pageTitle}
            ],        
        }));

        fetchFilters();

        return () => {
            setTableData({})
        }
    }, [])

    useEffect(() => {
        if(!updatedFilter){
            return;
        }

        fetch();

    }, [filter, updatedFilter]);

    const fetchEmployees = async (term, filterFetch) => {
        return fetchOptions('employee', searchParams, getUserCompanyAccess, getUserCompanyAccessById, setFilterOptions, term, filterFetch, 'user');
    }

    const fetchFilters = async () => {
        const employees = await fetchEmployees();
        employees.unshift({
            "id": -1,
            "fullName": "None",

        });

        setFilterOptions({
            employee: employees,
            fetched: 1,
        }); 
    }

    const filters = [
        {
            name: 'employee',
            nameKey: 'fullName',
            valueKey: 'id',
            optionsName: 'employee',
            label: strings.pages.hr.presence.presenceList.employee,
        }
    ]

    const onCancel = () => {
        setPageState(PageState.View);
    }

    const onFinish = () => {
        fetch();
        setPageState(PageState.View);
    }

    const fetch = () => {
        setTableData({
            ...tableData,
            loading: true
        });

        getAssetList({
            ...filter
        }).then(response => {
            if (!response || !response.ok) {
                return;
            }

            setTableData({
                loading: false,
                data: response.data.result,
                total: response.data.total
            });
        })
    }

    return <TablePageContext.Provider value={value}>
        <TablePage onFinish={() => onFinish()} deleteItem={deleteAsset} tableDescription={columns}
                tableData={tableData} filter={filter} filters={filters} filterOptions={filterOptions}
                referenceType={referenceType} permissionGroup={permissionGroup}
                tagPage={<Tag referenceType={ referenceType } data={ selectedItems } /> }
                editPage={<EditAsset data={ selectedItems }
                onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                addPage={<AddAsset onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                addButtonText={strings.components.tablePage.addButton.asset}
                />
    </TablePageContext.Provider>;

}

export default AssetList;
